import React, { useState, useEffect, useRef } from 'react';
import { AiOutlineLogout, AiOutlineDelete, AiOutlinePlus, AiOutlineApi, AiOutlineRight, AiOutlineTeam, AiOutlineHistory } from 'react-icons/ai';
import { Link } from 'react-router-dom';
import { TbFingerprint } from 'react-icons/tb';
import { BsPhone, BsLaptop } from 'react-icons/bs';
import { startRegistration } from '@simplewebauthn/browser';
import { apiUrl } from '../config/api';
import './ProfilePage.css';

// ── Inline PIN digit boxes ────────────────────────────────────────────────────
const PIN_LENGTH = 4;
const PinInput = ({ value, onChange, disabled, autoFocus }) => {
  const inputs = useRef([]);
  const handleKey = (e, idx) => {
    if (e.key === 'Backspace') {
      if (value[idx]) { const n = value.split(''); n[idx] = ''; onChange(n.join('')); }
      else if (idx > 0) { inputs.current[idx - 1]?.focus(); const n = value.split(''); n[idx - 1] = ''; onChange(n.join('')); }
      e.preventDefault();
    } else if (/^\d$/.test(e.key)) {
      const n = value.split(''); n[idx] = e.key; onChange(n.join(''));
      if (idx < PIN_LENGTH - 1) inputs.current[idx + 1]?.focus();
      e.preventDefault();
    }
  };
  const handlePaste = (e) => {
    const p = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, PIN_LENGTH);
    onChange(p.padEnd(PIN_LENGTH, '').slice(0, PIN_LENGTH));
    inputs.current[Math.min(p.length, PIN_LENGTH - 1)]?.focus();
    e.preventDefault();
  };
  return (
    <div className="pin-boxes">
      {Array.from({ length: PIN_LENGTH }).map((_, i) => (
        <input key={i} ref={(el) => (inputs.current[i] = el)}
          type="password" inputMode="numeric" pattern="\d*" maxLength={1}
          className={`pin-box${value[i] ? ' pin-box--filled' : ''}`}
          value={value[i] || ''} onChange={() => {}}
          onKeyDown={(e) => handleKey(e, i)} onPaste={handlePaste}
          disabled={disabled} autoFocus={autoFocus && i === 0}
          autoComplete="off" aria-label={`PIN digit ${i + 1}`} />
      ))}
    </div>
  );
};

// ── Pick an icon for a registered device based on its saved name ────────────
const DeviceIcon = ({ name, size = 15 }) => {
  if (/iphone|android|phone/i.test(name)) return <BsPhone size={size} />;
  if (/mac|laptop|macbook|pc|windows|desktop/i.test(name)) return <BsLaptop size={size} />;
  return <TbFingerprint size={size} />;
};

// ── Detect platform biometrics support ───────────────────────────────────────
async function isBiometricSupported() {
  if (!window.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable) return false;
  try { return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable(); }
  catch { return false; }
}

// ─────────────────────────────────────────────────────────────────────────────

const ProfilePage = ({ user, onLogout }) => {
  const displayName = user?.name || user?.email || 'User';
  const avatarLetter = displayName.charAt(0).toUpperCase();

  // ── PIN section ───────────────────────────────────────────────────────────
  const [showPinForm, setShowPinForm] = useState(false);
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [pinLoading, setPinLoading] = useState(false);
  const [pinError, setPinError] = useState('');
  const [pinSuccess, setPinSuccess] = useState('');

  // ── Biometric section ─────────────────────────────────────────────────────
  const [biometricSupported, setBiometricSupported] = useState(false);
  const [credentials, setCredentials] = useState([]);
  const [bioLoading, setBioLoading] = useState(false);
  const [bioError, setBioError] = useState('');
  const [bioSuccess, setBioSuccess] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [showDeviceNameInput, setShowDeviceNameInput] = useState(false);

  const token = localStorage.getItem('token');

  useEffect(() => {
    isBiometricSupported().then(setBiometricSupported);
    loadCredentials();
    // Set a sensible default device name based on user agent
    const ua = navigator.userAgent;
    if (/iPhone|iPad/.test(ua)) setDeviceName('iPhone');
    else if (/Android/.test(ua)) setDeviceName('Android phone');
    else if (/Mac/.test(ua)) setDeviceName('Mac');
    else setDeviceName('My device');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCredentials = async () => {
    try {
      const res = await fetch(apiUrl('/api/webauthn/credentials'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCredentials(data.credentials || []);
      }
    } catch { /* silent */ }
  };

  // ── Register new passkey ──────────────────────────────────────────────────
  const handleRegisterBiometric = async () => {
    setBioError(''); setBioSuccess(''); setBioLoading(true);
    try {
      // 1. Get registration challenge
      const challengeRes = await fetch(apiUrl('/api/webauthn/register/challenge'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Client-Origin': window.location.origin,
        },
      });
      if (!challengeRes.ok) throw new Error('Could not start registration');
      const challengeOptions = await challengeRes.json();

      // 2. Trigger browser/OS biometric prompt
      let credential;
      try {
        credential = await startRegistration(challengeOptions);
      } catch (err) {
        if (err.name === 'NotAllowedError') { setBioLoading(false); return; }
        throw err;
      }

      // 3. Verify with server
      const verifyRes = await fetch(apiUrl('/api/webauthn/register/verify'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Client-Origin': window.location.origin,
        },
        body: JSON.stringify({ credential, deviceName: deviceName || 'My device' }),
      });
      const verifyData = await verifyRes.json();
      if (!verifyRes.ok) throw new Error(verifyData.detail || verifyData.message || 'Registration failed');

      setBioSuccess(`"${deviceName || 'My device'}" registered — you can now use Face ID / fingerprint to sign in.`);
      setShowDeviceNameInput(false);
      await loadCredentials();
      setTimeout(() => setBioSuccess(''), 5000);
    } catch (err) {
      setBioError(err.message || 'Biometric registration failed');
    } finally {
      setBioLoading(false);
    }
  };

  // ── Remove a passkey ──────────────────────────────────────────────────────
  const handleDeleteCredential = async (id) => {
    setBioError('');
    try {
      const res = await fetch(apiUrl(`/api/webauthn/credentials/${id}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to remove device');
      await loadCredentials();
    } catch (err) {
      setBioError(err.message);
    }
  };

  // ── Change PIN ────────────────────────────────────────────────────────────
  const handleChangePin = async (e) => {
    e.preventDefault(); setPinError('');
    if (newPin.length < PIN_LENGTH) { setPinError(`Enter all ${PIN_LENGTH} digits`); return; }
    if (newPin !== confirmPin) { setPinError("PINs don't match"); setConfirmPin(''); return; }
    setPinLoading(true);
    try {
      const res = await fetch(apiUrl('/api/auth/set-pin'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ pin: newPin }),
      });
      const data = await res.json();
      if (!res.ok) { setPinError(data.detail || data.message || 'Failed to set PIN'); return; }
      localStorage.setItem('pinSet', 'true');
      localStorage.setItem('userEmail', user.email);
      setPinSuccess('PIN updated successfully');
      setNewPin(''); setConfirmPin('');
      setTimeout(() => { setPinSuccess(''); setShowPinForm(false); }, 2000);
    } catch { setPinError('Failed to connect to server.'); }
    finally { setPinLoading(false); }
  };

  return (
    <div className="pp-container">
      <div className="pp-header">
        <h1 className="pp-title">Profile</h1>
      </div>

      <div className="pp-card">
        {/* Identity */}
        <div className="pp-avatar">{avatarLetter}</div>
        <div className="pp-name">{displayName}</div>
        <div className="pp-email">{user?.email || ''}</div>
        <div className="pp-role-badge">
          {user?.isSuperAdmin ? 'Main administrator' : user?.isAdmin ? 'Administrator' : user?.isManager ? 'Manager' : 'User'}
        </div>

        {/* ── Biometric / Passkeys ───────────────────────────────────────── */}
        {biometricSupported && (
          <div className="pp-bio-section">
            <div className="pp-bio-head">
              <span className="pp-bio-icon-badge"><TbFingerprint size={18} /></span>
              <div className="pp-bio-head-text">
                <p className="pp-bio-title">Face ID &amp; fingerprint</p>
                <p className="pp-bio-subtitle">Sign in without typing your PIN.</p>
              </div>
              {credentials.length > 0 && (
                <span className="pp-bio-count">{credentials.length} device{credentials.length !== 1 ? 's' : ''}</span>
              )}
            </div>

            {/* Registered devices list */}
            {credentials.length > 0 ? (
              <ul className="pp-cred-list">
                {credentials.map((c) => (
                  <li key={c.id} className="pp-cred-row">
                    <span className="pp-cred-icon"><DeviceIcon name={c.deviceName} /></span>
                    <span className="pp-cred-name">{c.deviceName}</span>
                    <span className="pp-cred-date">
                      {c.lastUsedAt
                        ? `Used ${new Date(c.lastUsedAt).toLocaleDateString()}`
                        : `Added ${new Date(c.createdAt).toLocaleDateString()}`}
                    </span>
                    <button
                      type="button"
                      className="pp-cred-delete-btn"
                      onClick={() => handleDeleteCredential(c.id)}
                      aria-label={`Remove ${c.deviceName}`}
                    >
                      <AiOutlineDelete size={15} />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              !showDeviceNameInput && (
                <p className="pp-bio-empty">No devices set up yet — add this one to get started.</p>
              )
            )}

            {bioError && <p className="pp-pin-error">{bioError}</p>}
            {bioSuccess && <p className="pp-pin-success">{bioSuccess}</p>}

            {/* Add device */}
            {!showDeviceNameInput ? (
              <button
                type="button"
                className="pp-bio-add-btn"
                onClick={() => setShowDeviceNameInput(true)}
                disabled={bioLoading}
              >
                <AiOutlinePlus size={13} /> Add this device
              </button>
            ) : (
              <div className="pp-bio-name-panel">
                <input
                  type="text"
                  className="pp-bio-name-input"
                  value={deviceName}
                  onChange={(e) => setDeviceName(e.target.value)}
                  placeholder="Device name (e.g. iPhone 15)"
                  maxLength={40}
                  disabled={bioLoading}
                  autoFocus
                />
                <div className="pp-bio-name-actions">
                  <button
                    type="button"
                    className="pp-bio-cancel-btn"
                    onClick={() => setShowDeviceNameInput(false)}
                    disabled={bioLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="pp-bio-register-btn"
                    onClick={handleRegisterBiometric}
                    disabled={bioLoading}
                  >
                    {bioLoading ? 'Registering…' : 'Register device'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Staff pages — also the way in on phones, which have no sidebar ─────────── */}
        {(user?.isAdmin || user?.isManager) && (
          <>
            <Link to="/users" className="pp-admin-link">
              <span className="pp-bio-icon-badge"><AiOutlineTeam size={18} /></span>
              <span className="pp-admin-link-text">
                <span className="pp-bio-title">User Management</span>
                <span className="pp-bio-subtitle">Create and manage users</span>
              </span>
              <AiOutlineRight size={14} />
            </Link>
            <Link to="/removed-photos" className="pp-admin-link">
              <span className="pp-bio-icon-badge"><AiOutlineDelete size={18} /></span>
              <span className="pp-admin-link-text">
                <span className="pp-bio-title">Removed Photos</span>
                <span className="pp-bio-subtitle">Review and restore removed photos</span>
              </span>
              <AiOutlineRight size={14} />
            </Link>
          </>
        )}

        {/* ── Activity (admins only) ───────────────────────────────────────────── */}
        {user?.isAdmin && (
          <Link to="/activity" className="pp-admin-link">
            <span className="pp-bio-icon-badge"><AiOutlineHistory size={18} /></span>
            <span className="pp-admin-link-text">
              <span className="pp-bio-title">Activity</span>
              <span className="pp-bio-subtitle">Who added photos, and to which project</span>
            </span>
            <AiOutlineRight size={14} />
          </Link>
        )}

        {/* ── Integrations (admins only) ───────────────────────────────────────── */}
        {user?.isAdmin && (
          <Link to="/integrations" className="pp-admin-link">
            <span className="pp-bio-icon-badge"><AiOutlineApi size={18} /></span>
            <span className="pp-admin-link-text">
              <span className="pp-bio-title">Integrations</span>
              <span className="pp-bio-subtitle">API keys and API documentation</span>
            </span>
            <AiOutlineRight size={14} />
          </Link>
        )}

        {/* ── Change PIN ────────────────────────────────────────────────── */}
        <div className="pp-pin-section">
          {!showPinForm ? (
            <button className="pp-pin-toggle-btn"
              onClick={() => { setShowPinForm(true); setPinError(''); setPinSuccess(''); }}>
              Change PIN
            </button>
          ) : (
            <form className="pp-pin-form" onSubmit={handleChangePin} noValidate>
              <p className="pp-pin-form-label">New {PIN_LENGTH}-digit PIN</p>
              <PinInput value={newPin} onChange={(v) => { setNewPin(v); setPinError(''); }} disabled={pinLoading} autoFocus />
              <p className="pp-pin-form-label" style={{ marginTop: 14 }}>Confirm PIN</p>
              <PinInput value={confirmPin} onChange={(v) => { setConfirmPin(v); setPinError(''); }} disabled={pinLoading} />
              {pinError && <p className="pp-pin-error">{pinError}</p>}
              {pinSuccess && <p className="pp-pin-success">{pinSuccess}</p>}
              <div className="pp-pin-actions">
                <button type="button" className="pp-pin-cancel-btn"
                  onClick={() => { setShowPinForm(false); setNewPin(''); setConfirmPin(''); setPinError(''); }}
                  disabled={pinLoading}>Cancel</button>
                <button type="submit" className="pp-pin-save-btn"
                  disabled={pinLoading || newPin.length < PIN_LENGTH || confirmPin.length < PIN_LENGTH}>
                  {pinLoading ? 'Saving…' : 'Save PIN'}
                </button>
              </div>
            </form>
          )}
        </div>

        <button className="pp-logout-btn" onClick={onLogout}>
          <AiOutlineLogout size={16} />
          Sign out
        </button>
      </div>
    </div>
  );
};

export default ProfilePage;
